import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "./Controls";

type Step = { route?: string; target?: string; title: string; body: string };

// target: data-testid del elemento a resaltar; con "|" se prueban varios (escritorio | celular).
const ADMIN_STEPS: Step[] = [
  { title: "Bienvenido a la plataforma", body: "Esta guía recorre las pantallas principales en un par de minutos. Se puede cerrar en cualquier momento y volver a abrir con el botón Guía." },
  { route: "/piezas", target: "nav-parts|bottom-nav-parts", title: "Piezas", body: "Acá están los números de parte que se inspeccionan, con su foto de referencia, sus fotos de entrenamiento y su modelo de IA." },
  { route: "/piezas", target: "new-part-button|empty-new-part-button", title: "Cargar una pieza nueva", body: "Número de parte, nombre, de 1 a 10 puntos de inspección y una foto de referencia. No hace falta programar nada." },
  { route: "/piezas", target: "parts-search-input", title: "Buscar y abrir una pieza", body: "Se busca por número de parte o nombre. Dentro de cada pieza se suben sus fotos OK y NG y se entrena la IA con un botón." },
  { route: "/operarios", target: "new-operator-button", title: "Operarios", body: "Cada operario tiene su usuario, su PIN y su gafete. Desde acá se dan de alta y se les asigna la pieza que inspeccionan." },
  { route: "/registrar", target: "register-card|register-part-select", title: "Registrar una inspección", body: "La pantalla del operario: la foto de referencia y los puntos de la pieza, uno por uno." },
  { route: "/registrar", target: "register-good-button", title: "Buena o Mala", body: "Con un toque se guarda la decisión a nombre de quien la hizo, con hora y dispositivo. La foto y el lote son opcionales." },
  { route: "/inspecciones", target: "inspections-filters", title: "Inspecciones en vivo", body: "El listado se actualiza solo. Se filtra por fecha, operario, pieza, punto, resultado o discrepancias. Al tocar una fila se ven la foto y todos sus datos." },
  { route: "/inspecciones", target: "export-csv-button", title: "Exportar a planilla", body: "Descarga en CSV lo que muestra el filtro, para análisis o para el estudio R&R." },
  { route: "/indicadores", target: "period-7d|metrics-part-select", title: "Indicadores", body: "Inspecciones, piezas completas, malas según el operario y según la IA, y discrepancias. El período y la pieza se eligen acá." },
  { route: "/ajustes", target: "demo-data-block|change-my-pin-block", title: "Ajustes", body: "Cambiar el PIN propio y manejar los datos de ejemplo: cargarlos, recargarlos o borrarlos antes de empezar con datos reales." },
  { title: "Listo", body: "Ese es el recorrido. El botón Guía, arriba a la derecha, la vuelve a abrir cuando haga falta." },
];

const OPERATOR_STEPS: Step[] = [
  { title: "Bienvenido", body: "Esta guía muestra cómo registrar una inspección. Se puede cerrar en cualquier momento y volver a abrir con el botón Guía." },
  { route: "/registrar", target: "register-point-title|register-no-part", title: "El punto a revisar", body: "Arriba se ve qué punto toca inspeccionar. La foto de referencia muestra dónde está en la pieza." },
  { route: "/registrar", target: "register-point-chip-0", title: "Los puntos de la pieza", body: "Se recorren en orden, o se toca uno para ir directo. Los que ya se registraron se marcan en verde." },
  { route: "/registrar", target: "register-photo-label", title: "Foto opcional", body: "Se puede sumar una foto de la pieza con la cámara del celular." },
  { route: "/registrar", target: "register-good-button", title: "Buena o Mala", body: "Con un toque se guarda la decisión a tu nombre. Al completar todos los puntos, la pieza cuenta como terminada." },
  { route: "/inspecciones", target: "nav-inspections|bottom-nav-inspections", title: "Mis inspecciones", body: "Acá se ven las inspecciones registradas, con su hora y su resultado." },
  { title: "Listo", body: "Ese es el recorrido. El botón Guía, arriba a la derecha, la vuelve a abrir cuando haga falta." },
];

type Rect = { top: number; left: number; width: number; height: number };
const PAD = 8;
const CARD_W = 360;

function findTarget(target?: string): HTMLElement | null {
  if (!target) return null;
  for (const id of target.split("|")) {
    const el = document.querySelector<HTMLElement>(`[data-testid="${id}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

export function GuidedTour({ open, role, onClose }: { open: boolean; role: string; onClose: () => void }) {
  const steps = role === "admin" ? ADMIN_STEPS : OPERATOR_STEPS;
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const step = steps[Math.min(i, steps.length - 1)];
  const last = i === steps.length - 1;

  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  // Lleva a la pantalla del paso.
  useEffect(() => {
    if (open && step.route && location.pathname !== step.route) navigate(step.route);
  }, [open, step, location.pathname, navigate]);

  // Espera a que aparezca el elemento, lo centra y lo mide.
  useEffect(() => {
    if (!open) return undefined;
    setReady(false);
    setRect(null);
    let tries = 0;
    let timer: number | undefined;
    let el: HTMLElement | null = null;
    const measure = () => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    const look = () => {
      const onRoute = !step.route || window.location.pathname === step.route;
      el = onRoute ? findTarget(step.target) : null;
      if ((!onRoute || (step.target && !el)) && tries++ < 40) {
        timer = window.setTimeout(look, 100);
        return;
      }
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        timer = window.setTimeout(() => {
          measure();
          setReady(true);
        }, 380);
      } else {
        setReady(true);
      }
    };
    look();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step]);

  const next = useCallback(() => {
    if (last) onClose();
    else setI((n) => n + 1);
  }, [last, onClose]);
  const prev = useCallback(() => setI((n) => Math.max(0, n - 1)), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, next, prev]);

  useEffect(() => {
    if (open && ready) document.querySelector<HTMLButtonElement>('[data-testid="tour-next-button"]')?.focus();
  }, [open, ready, i]);

  if (!open) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const mobile = vw < 640;
  const spot = ready && rect;
  let cardStyle: React.CSSProperties;
  if (!spot) {
    cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  } else if (mobile) {
    cardStyle = rect.top + rect.height / 2 > vh / 2 ? { left: 16, right: 16, top: 16 } : { left: 16, right: 16, bottom: 76 };
  } else {
    const left = Math.min(Math.max(16, rect.left), vw - CARD_W - 16);
    cardStyle = rect.top + rect.height + 12 + 230 < vh ? { left, top: rect.top + rect.height + 12 } : { left, top: Math.max(16, rect.top - 12), transform: "translateY(-100%)" };
  }

  return createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="guided-tour">
      {spot ? (
        <div
          className="pointer-events-none fixed rounded-xl outline outline-2 outline-mark motion-safe:transition-all motion-safe:duration-300"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height, boxShadow: "0 0 0 9999px rgba(22, 28, 39, 0.62)" }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="fixed inset-0 bg-ink/60" />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        className="fixed w-[calc(100vw-32px)] rounded-2xl bg-white p-5 shadow-pop sm:w-[360px]"
        style={cardStyle}
        data-testid="tour-card"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500" data-testid="tour-progress">Paso {i + 1} de {steps.length}</span>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-ink" aria-label="Cerrar guía" data-testid="tour-close-button">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 id="tour-title" className="mt-2 font-display text-xl font-bold text-ink">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
        <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-mark motion-safe:transition-all" style={{ width: `${((i + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={prev} disabled={i === 0} data-testid="tour-prev-button">
            <ChevronLeft className="h-4 w-4" /> Anterior
          </Button>
          <Button size="sm" onClick={next} data-testid="tour-next-button">
            {last ? "Terminar" : <>Siguiente <ChevronRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
